import React, {useState} from 'react';
import {Box, Button, Checkbox, FormControlLabel, Stack, Typography} from '@mui/material';
import AdmicityGroupFilterSelect from '../../../../shared-components/AdmicityGroupFilterSelect';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import IconButton from '@mui/material/IconButton';
import {mergeArraysByKey} from '../../../../utility/mergeArraysByKey';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import SettingsSectionBase from '../../Forms/PrivateFormSettings/SettingsSection';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';

/* eslint-disable react/prop-types */

const groupFiltersTableColumns = [
    {
        accessorKey: 'displayName',
        header: 'Group Name',
        grow: true,
    },
    {
        accessorKey: 'excluded',
        header: 'Status',
        size: 50,
        grow: false,
        Cell: ({cell}) => cell.getValue() === true ? 'Excluded' : 'Included'
    }
];

const upwardAutocompletePopperModifiers = [
    {
        name: 'flip',
        enabled: false
    }
];

const groupFiltersTableMaxHeight = 'calc(56px + (5 * 52px))';

const FiltersTab = ({
                        data,
                        settings,
                        strictRecipientsFiltering,
                        setStrictRecipientsFiltering,
                        setSettings,
                        disableStrictRecipientsFiltering = false,
                        disableEnquirySelect = false,
                        hideStudentSelect = false,
                        hideApplicationSelect = false,
                        memberType = GROUP_MEMBERS_TYPE.students,
                        selectionSubHeader,
                        groupFiltersLabel,
                        groupFilterSelectLabel,
                        employeeAutocompleteDisablePortal = false,
                        employeeAutocompletePlacement = 'top-start',
                        employeeAutocompletePopperModifiers = upwardAutocompletePopperModifiers,
                        applicationAutocompleteDisablePortal = false,
                        applicationAutocompletePlacement = 'top-start',
                        applicationAutocompletePopperModifiers = upwardAutocompletePopperModifiers,
                        studentAutocompleteDisablePortal = false,
                        studentAutocompletePlacement = 'top-start',
                        studentAutocompletePopperModifiers = upwardAutocompletePopperModifiers,
                        enquiryAutocompleteDisablePortal = false,
                        enquiryAutocompletePlacement = 'top-start',
                        enquiryAutocompletePopperModifiers = upwardAutocompletePopperModifiers
                    }) => {
    const [filter, setFilter] = useState({});
    const isFilterNotSelected = filter == null || !filter.filterId;
    const rootFilterIds = data ? data.map(x => x.id) : [];

    const handleAddGroupFilter = (isExcluded) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(x => x.name).join(' - '),
            excluded: isExcluded
        };

        setSettings(prev => ({
            ...prev,
            groupFilters: mergeArraysByKey([...settings.groupFilters, newFilter], settings.groupFilters)
        }));
        setFilter(null);
    };
    const memberTypeTitle =
        memberType === GROUP_MEMBERS_TYPE.employee ? 'Employee' : 'Student';

    const icon =
        memberTypeTitle === 'Employee'
            ? <AssignmentIndOutlinedIcon fontSize="small"/>
            : memberTypeTitle === 'Student'
                ? <PeopleOutlineOutlinedIcon fontSize="small"/>
                : <Groups2OutlinedIcon fontSize="small"/>;

    const plural =
        memberTypeTitle === 'Employee'
            ? 'Employees'
            : memberTypeTitle === 'Student'
                ? 'Students'
                : 'Members';
    const resolvedSelectionSubHeader =
        selectionSubHeader ?? `Select ${memberTypeTitle.toLowerCase()} filters, groups and/or individual ${plural.toLowerCase()}.`;
    const resolvedGroupFiltersLabel = groupFiltersLabel ?? `${memberTypeTitle} Filters or Groups`;
    const resolvedGroupFilterSelectLabel = groupFilterSelectLabel ?? 'Groups';
    const hasSelectedIndividuals =
        memberType === GROUP_MEMBERS_TYPE.employee
            ? !!settings.employeeFilters?.length
            : !!settings.studentFilters?.length ||
            !!settings.applicationFilters?.length ||
            !!settings.enquiryFilters?.length;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <SettingsSectionBase
                header={`${memberTypeTitle} Selection`}
                headerIcon={icon}
                subHeader={resolvedSelectionSubHeader}
            >

                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="subtitle1" fontWeight="bold">{resolvedGroupFiltersLabel}</Typography>
                    <Box>
                        <AdmicityGroupFilterSelect
                            isRequired={!hasSelectedIndividuals}
                            sx={{width: '100%'}}
                            data={data}
                            label={resolvedGroupFilterSelectLabel}
                            value={filter?.filterId || ''}
                            onChange={(filterId, paths) => setFilter({filterId, paths})}
                        />
                        <Stack alignItems="center" flexDirection="row" gap={1} justifyContent={'end'} pt={1}>
                            <Button
                                onClick={() => handleAddGroupFilter(true)}
                                variant="outlined"
                                disabled={isFilterNotSelected || rootFilterIds.includes(filter.filterId)}
                            >
                                Exclude
                            </Button>
                            <Button
                                onClick={() => handleAddGroupFilter(false)}
                                variant="contained"
                                disabled={!filter?.filterId}
                                sx={{
                                    backgroundColor: '#186BBE',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Include
                            </Button>
                        </Stack>
                    </Box>
                </Box>
                {
                    settings.groupFilters?.length > 0 && <Box sx={{pt: 1}}>
                        <AdmicityTableV2
                            columns={groupFiltersTableColumns}
                            data={settings.groupFilters}
                            tableProps={{
                                layoutMode: 'grid-no-grow',
                                enableRowActions: true,
                                enableSorting: false,
                                enableFilters: false,
                                enableHiding: false,
                                enableFullScreenToggle: false,
                                enableColumnResizing: false,
                                enableBottomToolbar: false,
                                enableTopToolbar: false,
                                muiTableBodyProps: {
                                    sx: {
                                        '& :last-child td': {
                                            borderBottom: 0,
                                        },
                                    }
                                },
                                muiTablePaperProps: {
                                    variant: 'outlined',
                                    elevation: 0,
                                    sx: {
                                        display: 'block',
                                        flexDirection: 'unset',
                                        flexGrow: 0,
                                        width: '100%',
                                        height: 'auto',
                                    }
                                },
                                muiTableContainerProps: {
                                    sx: {
                                        flex: 'unset',
                                        maxHeight: groupFiltersTableMaxHeight,
                                        minHeight: 'auto',
                                        border: 'none',
                                        overflow: 'auto',
                                    },
                                    style: {
                                        minHeight: 'auto',
                                    },
                                },
                                renderRowActions: ({row}) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const filters = settings.groupFilters.filter(filter => row.original.id !== filter.id);

                                            setSettings(prev => ({
                                                ...prev,
                                                groupFilters: filters
                                            }));
                                        }}>
                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                    </IconButton>
                                )
                            }}
                        />
                        {settings.groupFilters.length > 1 && (
                            <FormControlLabel
                                sx={{
                                    pt: 1,
                                    pb: 1,
                                }}
                                disabled={disableStrictRecipientsFiltering}
                                control={
                                    <Checkbox
                                        checked={strictRecipientsFiltering}
                                        onChange={(e) => setStrictRecipientsFiltering(e.target.checked)}
                                    />
                                }
                                label="Must appear in all these filters"
                            />
                        )}
                    </Box>
                }
                <Box display="flex" flexDirection="column" gap={2}>
                    {memberType === GROUP_MEMBERS_TYPE.employee
                        ? (
                            <>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Individual Employees
                                </Typography>
                                <PaginatedAutocomplete
                                    textFieldProps={{
                                        label: 'Employees',
                                        placeholder: 'Enter name, surname, or full name',
                                        required: true,
                                    }}
                                    disablePortal={employeeAutocompleteDisablePortal}
                                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                                    getOptionLabel={(option) => option.label || `${option.preferredForename} ${option.preferredSurname}`}
                                    popperPlacement={employeeAutocompletePlacement}
                                    popperModifiers={employeeAutocompletePopperModifiers}
                                    value={settings.employeeFilters || []}
                                    onChange={(e, newValue) =>
                                        setSettings((prev) => ({...prev, employeeFilters: newValue}))
                                    }
                                />
                            </>
                        )
                        : (
                            <>
                                {!hideApplicationSelect && (
                                    <>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Individual Students
                                        </Typography>
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Applicants',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            disablePortal={applicationAutocompleteDisablePortal}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'applicant',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            popperPlacement={applicationAutocompletePlacement}
                                            popperModifiers={applicationAutocompletePopperModifiers}
                                            value={settings.applicationFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, applicationFilters: newValue}))
                                            }
                                        />
                                    </>
                                )}
                                    {!hideStudentSelect && (
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Students',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            disablePortal={studentAutocompleteDisablePortal}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'student',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            popperPlacement={studentAutocompletePlacement}
                                            popperModifiers={studentAutocompletePopperModifiers}
                                            value={settings.studentFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, studentFilters: newValue}))
                                            }
                                        />
                                    )}
                                    {!disableEnquirySelect && (
                                        <PaginatedAutocomplete
                                            textFieldProps={{
                                                label: 'Enquiries',
                                                placeholder: 'Enter name, surname, or full name',
                                                required: true,
                                            }}
                                            disablePortal={enquiryAutocompleteDisablePortal}
                                            fetchFn={useLazyGetStudentFiltersQuery}
                                            fetchFnProps={{
                                                queryParams: {
                                                    pupilType: 'enquiry',
                                                },
                                            }}
                                            getOptionLabel={(option) => option.label}
                                            popperPlacement={enquiryAutocompletePlacement}
                                            popperModifiers={enquiryAutocompletePopperModifiers}
                                            value={settings.enquiryFilters}
                                            onChange={(e, newValue) =>
                                                setSettings((prev) => ({...prev, enquiryFilters: newValue}))
                                            }
                                        />
                                    )}
                            </>
                        )}
                </Box>
            </SettingsSectionBase>
        </Box>
    );
};

export default FiltersTab;
